import { createFileRoute } from "@tanstack/react-router";
import {
    TbAdjustments,
    TbAlertTriangle,
    TbBuilding,
    TbChartBar,
    TbChartPie,
    TbClipboardText,
    TbDownload,
    TbFileText,
    TbPhoneCall,
    TbRobot,
    TbSearch,
    TbSparkles,
} from "react-icons/tb";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon, Input } from "@elit-hair/elit-ui";
import { Link as AppLink } from "@/components/link";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_INDEX)({
    component: DeveloperIndexPage,
});

const AI_EXPERIMENTS = [
    {
        id: "dimension-definitions",
        titleKey: "dimension_definitions",
        descriptionKey: "developer_dimension_definitions_description",
        to: APP_ROUTES_TO.AI_DIMENSION_DEFINITIONS,
        icon: TbClipboardText,
    },
    {
        id: "dimensions",
        titleKey: "dimensions",
        descriptionKey: "developer_dimensions_description",
        to: APP_ROUTES_TO.AI_DIMENSIONS,
        icon: TbAdjustments,
    },
] as const;

const OPERATIONS_EXPERIMENTS = [
    {
        id: "kpis-report",
        titleKey: "developer_kpis_report_title",
        descriptionKey: "developer_kpis_report_description",
        to: APP_ROUTES_TO.DEVELOPER_KPIS_REPORT,
        icon: TbChartBar,
    },
    {
        id: "bulk-call-actions",
        titleKey: "developer_bulk_call_actions_title",
        descriptionKey: "developer_bulk_call_actions_description",
        to: APP_ROUTES_TO.DEVELOPER_BULK_CALL_ACTIONS,
        icon: TbSparkles,
    },
    {
        id: "calls-by-phone",
        titleKey: "developer_calls_by_phone_title",
        descriptionKey: "developer_calls_by_phone_description",
        to: APP_ROUTES_TO.DEVELOPER_CALLS_BY_PHONE,
        icon: TbPhoneCall,
    },
    {
        id: "telli",
        titleKey: "developer_telli_title",
        descriptionKey: "developer_telli_description",
        to: APP_ROUTES_TO.DEVELOPER_TELLI,
        icon: TbRobot,
    },
] as const;

const INCIDENTS_EXPERIMENTS = [
    {
        id: "incidents",
        titleKey: "incidents",
        descriptionKey: "developer_incidents_description",
        to: APP_ROUTES_TO.INCIDENTS,
        icon: TbAlertTriangle,
    },
    {
        id: "incident-analytics",
        titleKey: "developer_incident_analytics_title",
        descriptionKey: "developer_incident_analytics_description",
        to: APP_ROUTES_TO.DEVELOPER_INCIDENT_ANALYTICS,
        icon: TbChartPie,
    },
] as const;

const DATA_EXPORTS_EXPERIMENTS = [
    {
        id: "data-exports",
        titleKey: "data_exports",
        descriptionKey: "developer_data_exports_description",
        to: APP_ROUTES_TO.DEVELOPER_DATA_EXPORTS,
        icon: TbDownload,
    },
] as const;

const EXPERIMENTS_EXPERIMENTS = [
    {
        id: "custom-pages",
        titleKey: "custom_pages",
        descriptionKey: "developer_custom_pages_description",
        to: APP_ROUTES_TO.MANAGEMENT_CUSTOM_PAGES,
        icon: TbFileText,
    },
    {
        id: "office",
        titleKey: "office",
        descriptionKey: "developer_office_description",
        to: APP_ROUTES_TO.MANAGEMENT_OFFICE,
        icon: TbBuilding,
    },
] as const;

const SECTIONS = [
    { id: "ai", titleKey: "ai", items: AI_EXPERIMENTS },
    { id: "incidents", titleKey: "incidents", items: INCIDENTS_EXPERIMENTS },
    { id: "data-exports", titleKey: "data_exports", items: DATA_EXPORTS_EXPERIMENTS },
    { id: "experiments", titleKey: "developer_experiments", items: EXPERIMENTS_EXPERIMENTS },
    { id: "operations", titleKey: "developer_operations", items: OPERATIONS_EXPERIMENTS },
] as const;

function DeveloperIndexPage() {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");

    const filteredSections = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        if (!query) {
            return SECTIONS;
        }

        return SECTIONS.map((section) => ({
            ...section,
            items: section.items.filter((exp) => {
                const title = t(exp.titleKey).toLowerCase();
                const description = t(exp.descriptionKey).toLowerCase();
                return title.includes(query) || description.includes(query);
            }),
        })).filter((section) => section.items.length > 0);
    }, [searchTerm, t]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-center">
                <div className="relative w-full max-w-xl">
                    <Icon
                        icon={TbSearch}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder={t("search")}
                        className="h-10 rounded-xl border-border/70 pl-10"
                    />
                </div>
            </div>

            {filteredSections.map((section) => (
                <section key={section.id} className="space-y-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(section.titleKey)}
                    </h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {section.items.map((exp) => {
                            const cardIcon = exp.icon;
                            return (
                                <AppLink
                                    key={exp.id}
                                    to={exp.to}
                                    className="group flex h-full flex-col gap-3 rounded-xl border border-border/70 bg-card/95 p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-card hover:shadow-md hover:shadow-primary/10"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                                            <Icon
                                                icon={cardIcon}
                                                size={20}
                                                className="transition-transform duration-200 group-hover:scale-105"
                                            />
                                        </div>
                                        <h3 className="font-semibold tracking-tight transition-colors group-hover:text-primary">
                                            {t(exp.titleKey)}
                                        </h3>
                                    </div>
                                    <p className="text-sm leading-relaxed text-muted-foreground">{t(exp.descriptionKey)}</p>
                                </AppLink>
                            );
                        })}
                    </div>
                </section>
            ))}

            {filteredSections.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
                    {t("no_entities_found")}
                </div>
            ) : null}
        </div>
    );
}
